'use client'

import { useState } from 'react'
import { InputsPanel } from './InputsPanel'
import { AssumptionsNote, ResultsPanel } from './ResultsPanel'
import { ResultsEmailedNote, UnlockGate } from './UnlockGate'
import { useLeadMagnetUnlocked } from '../../../hooks/use-lead-magnet-unlocked'
import { setLeadMagnetUnlockCookie } from '../../../utils/lead-magnet-unlock'
import { useCalculator } from '../_hooks/use-calculator'
import type { CreateCareerBreakLeadFn } from '../types'

/** Cookie `career-break_unlocked` — same convention as the lead magnets. */
export const UNLOCK_SLUG = 'career-break'

/** `defaultUnlocked` exists so the VRT story can capture the results; the page never sets it. */
export function CareerBreakCalculator({
  createLead,
  defaultUnlocked = false,
}: {
  createLead: CreateCareerBreakLeadFn
  defaultUnlocked?: boolean
}) {
  const { state, set, results } = useCalculator()
  // Only true after a submit in this session, so a returning visitor is not told an email
  // is on its way when nothing was sent this time.
  const [emailed, setEmailed] = useState(false)

  // The page is statically rendered, so the server cannot see the cookie: the server
  // snapshot stays locked and the client re-reads after hydration.
  const cookieUnlocked = useLeadMagnetUnlocked(UNLOCK_SLUG)
  const unlocked = defaultUnlocked || emailed || cookieUnlocked

  const handleUnlock = () => {
    // Survives a refresh, so a returning visitor is not asked to submit a second time,
    // which would create a duplicate Lead.
    setLeadMagnetUnlockCookie(UNLOCK_SLUG)
    setEmailed(true)
  }

  return (
    <div className="bg-background">
      <div className="mx-auto flex max-w-screen-lg flex-col gap-4 px-3.5 pt-6 pb-12 tablet:grid tablet:grid-cols-2 tablet:items-start tablet:gap-5 tablet:px-6">
        <InputsPanel state={state} set={set} results={results} />
        <div className="flex flex-col gap-4 tablet:sticky tablet:top-4">
          {/* Do not mount results until unlocked — blur/inert is not an access control. */}
          {unlocked ? (
            <div data-testid="career-break-results">
              <ResultsPanel state={state} results={results} />
            </div>
          ) : (
            <UnlockGate createLead={createLead} state={state} onUnlock={handleUnlock} />
          )}
          {emailed && <ResultsEmailedNote />}
        </div>
      </div>
      <div className="px-3.5 pb-12 tablet:px-6">
        <AssumptionsNote />
      </div>
    </div>
  )
}
